/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import { Loader2, CheckCircle, Clock } from "lucide-react";

interface SyncIndicatorProps {
  lastUpdated: Date | null;
  isLoading: boolean;
  isPolling: boolean;
  intervalMs: number;
}

export function SyncIndicator({
  lastUpdated,
  isLoading,
  isPolling,
  intervalMs,
}: SyncIndicatorProps) {
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  const [nextRefresh, setNextRefresh] = useState<number>(0);

  useEffect(() => {
    if (!lastUpdated || !isPolling) return;

    const updateTimes = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - lastUpdated.getTime()) / 1000);
      const nextRefreshIn = Math.max(
        0,
        Math.ceil((intervalMs - (now - lastUpdated.getTime())) / 1000),
      );

      setTimeElapsed(elapsed);
      setNextRefresh(nextRefreshIn);
    };

    updateTimes();
    const interval = setInterval(updateTimes, 1000);

    return () => clearInterval(interval);
  }, [lastUpdated, isPolling, intervalMs]);

  if (!isPolling) {
    return null;
  }

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      {isLoading ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin text-orange-500" />
          <span>Syncing...</span>
        </>
      ) : lastUpdated ? (
        <>
          <CheckCircle className="h-3 w-3 text-green-500" />
          <span>
            Updated{" "}
            {timeElapsed === 0 ? "now" : `${formatTime(timeElapsed)} ago`}
          </span>
        </>
      ) : (
        <>
          <div className="h-3 w-3 rounded-full bg-gray-400" />
          <span>Initializing...</span>
        </>
      )}
    </div>
  );
}
